import { useCallback, useEffect, useState } from "react";
import { Layers, Edit, Trash, Plus } from "lucide-react";
import { Select } from "antd";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Input, Textarea } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Reactable } from "../../components/ui/reactable";
import { DialogModal, ConfirmPopupModal } from "../../components/ui/dialogModal";
import { antSelectClass, modalInputClass, modalTextareaClass, modalDescriptionFieldProps } from "../../components/ui/fieldStyles";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { useInternshipPermissions } from "../../hooks/useInternshipPermissions";
import {
    INTERNSHIP_PROGRAMS,
    INTERNSHIP_PHASES,
    TRAINING_TASKS,
    EMPLOYEE_DEPARTMENT,
    EMPLOYEE_PERSONAL_INFO,
} from "../../constants/api";
import { ModalField, employeeOption } from "./internshipUi";

const EMPTY_PROGRAM = {
    id: null,
    name: "",
    description: "",
    department_id: null,
    duration_weeks: "",
    default_mentor_id: null,
};

const EMPTY_PHASE = {
    id: null,
    name: "",
    description: "",
    sequence: 1,
    duration_weeks: "",
    task_ids: [],
};

const SELECT_CLASS = antSelectClass();

const withCounts = (list) => (list || []).map((p) => ({ ...p, phase_count: p.phases?.length || 0 }));

const InternshipPrograms = () => {
    const { fetch, create, update, remove } = useApiMethods();
    const { canManagePrograms } = useInternshipPermissions();

    const [programs, setPrograms] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [programForm, setProgramForm] = useState(null);
    const [phaseForm, setPhaseForm] = useState(null);
    const [phaseProgram, setPhaseProgram] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const loadPrograms = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(INTERNSHIP_PROGRAMS);
            setPrograms(withCounts(res?.data));
        } catch (_) {
            ToastMsg("error", "Failed to load internship programs");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadLookups = useCallback(async () => {
        try {
            const [dep, emp, tsk] = await Promise.all([
                fetch(EMPLOYEE_DEPARTMENT, { drop_down: "True" }),
                fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: "True" }),
                fetch(TRAINING_TASKS),
            ]);
            setDepartments((dep?.data || []).map((d) => ({ value: d.id, label: d.department_name })));
            setEmployees((emp?.data || []).filter((e) => e.is_active).map(employeeOption));
            setTasks((tsk?.data || []).map((t) => ({ value: t.id, label: t.title })));
        } catch (_) { /* lookups are non-fatal */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadPrograms();
        loadLookups();
    }, [loadPrograms, loadLookups]);

    const refreshPhaseProgram = (list, programId) => {
        const next = (list || []).find((p) => p.id === programId);
        setPhaseProgram(next || null);
    };

    const saveProgram = async () => {
        if (!programForm.name.trim()) {
            ToastMsg("error", "Please enter a program name");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...programForm,
                duration_weeks: programForm.duration_weeks ? Number(programForm.duration_weeks) : null,
            };
            const res = programForm.id
                ? await update(INTERNSHIP_PROGRAMS, payload)
                : await create(INTERNSHIP_PROGRAMS, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || (programForm.id ? "Program updated" : "Program created"));
                const createdId = programForm.id ? programForm.id : res?.data?.id;
                const wasCreate = !programForm.id;
                setProgramForm(null);
                const latest = await fetch(INTERNSHIP_PROGRAMS);
                const list = withCounts(latest?.data);
                setPrograms(list);
                if (wasCreate && createdId) refreshPhaseProgram(list, createdId);
            }
        } finally {
            setSaving(false);
        }
    };

    const savePhase = async () => {
        if (!phaseForm.name.trim()) {
            ToastMsg("error", "Please enter a phase name");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...phaseForm,
                duration_weeks: phaseForm.duration_weeks ? Number(phaseForm.duration_weeks) : null,
            };
            const res = phaseForm.id
                ? await update(INTERNSHIP_PHASES, payload)
                : await create(INTERNSHIP_PHASES, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || (phaseForm.id ? "Phase updated" : "Phase added"));
                const programId = phaseForm.program_id;
                setPhaseForm(null);
                const latest = await fetch(INTERNSHIP_PROGRAMS);
                const list = withCounts(latest?.data);
                setPrograms(list);
                refreshPhaseProgram(list, programId);
            }
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        const { kind, id } = deleteTarget;
        const url = kind === "program" ? INTERNSHIP_PROGRAMS : INTERNSHIP_PHASES;
        const res = await remove(`${url}?id=${id}`);
        setDeleteTarget(null);
        if (res?.status === "success") {
            ToastMsg("success", res.message || "Removed");
            const latest = await fetch(INTERNSHIP_PROGRAMS);
            const list = withCounts(latest?.data);
            setPrograms(list);
            if (kind === "phase" && phaseProgram) {
                refreshPhaseProgram(list, phaseProgram.id);
            }
            if (kind === "program") setPhaseProgram(null);
        }
    };

    const columns = [
        {
            accessor: "name", Header: "Program", sortable: true, filterable: true,
            Cell: ({ value }) => <span className="font-medium text-foreground">{value || "-"}</span>,
        },
        {
            accessor: "department", Header: "Department", sortable: true, filterable: true,
            filterType: "select",
            filterOptions: departments.map((d) => ({ value: d.label, label: d.label })),
            Cell: ({ value }) => value || "All departments",
        },
        {
            accessor: "duration_weeks", Header: "Duration", sortable: true,
            Cell: ({ value }) => (value ? `${value} weeks` : "-"),
        },
        {
            accessor: "phase_count", Header: "Phases", sortable: true,
            Cell: ({ value }) => <Badge variant="secondary">{value ?? 0}</Badge>,
        },
        {
            accessor: "enrolment_count", Header: "Enrolled", sortable: true,
            Cell: ({ value }) => <Badge variant="secondary">{value ?? 0}</Badge>,
        },
        {
            accessor: "default_mentor", Header: "Default Mentor", sortable: true, filterable: true,
            Cell: ({ value }) => value || "Not assigned",
        },
        ...(canManagePrograms ? [{
            accessor: "actions", Header: "Actions",
            Cell: ({ row }) => (
                <div className="flex items-center gap-1">
                    <Button
                        size="sm"
                        variant="ghost"
                        title="Manage phases"
                        onClick={(e) => { e.stopPropagation(); setPhaseProgram(row); }}
                    >
                        <Layers className="h-4 w-4" />
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        title="Edit program"
                        onClick={(e) => {
                            e.stopPropagation();
                            setProgramForm({
                                id: row.id,
                                name: row.name,
                                description: row.description || "",
                                department_id: row.department_id,
                                duration_weeks: row.duration_weeks ?? "",
                                default_mentor_id: row.default_mentor_id,
                            });
                        }}
                    >
                        <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-600"
                        title="Delete program"
                        onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({ kind: "program", id: row.id, name: row.name });
                        }}
                    >
                        <Trash className="h-4 w-4" />
                    </Button>
                </div>
            ),
        }] : []),
    ];

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={Layers}
                title="Internship Programs"
                subtitle="Phases and the trainings an intern works through"
                totalRecords={programs.length}
                buttonEnable={canManagePrograms}
                setOpenDialog={() => setProgramForm({ ...EMPTY_PROGRAM })}
                buttonName="Add Program"
            />

            {loading ? (
                <div className="text-center py-10 text-muted-foreground">Loading...</div>
            ) : (
                <Reactable
                    pagename="internship-programs"
                    data={programs}
                    setData={setPrograms}
                    columns={columns}
                    isCSVRequired
                    isPDFRequired
                    url={INTERNSHIP_PROGRAMS}
                    rowSize="sm"
                    tableHeight="calc(100vh - 280px)"
                    onRowClick={(row) => setPhaseProgram(row)}
                />
            )}

            <DialogModal
                open={!!programForm}
                onClose={() => setProgramForm(null)}
                title={programForm?.id ? "Edit Program" : "Add Program"}
                onSave={saveProgram}
                saveLabel={programForm?.id ? "Update" : "Save"}
                closeLabel="Cancel"
                editMode={!!programForm?.id}
                loading={saving}
                width="600px"
            >
                {programForm && (
                    <form className="space-y-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {programForm.id
                                ? "Update the program details. Phases and trainings are managed from the Phases dialog."
                                : "Create the program first, then add ordered phases and the trainings each intern should complete."}
                        </p>
                        <ModalField label="Program name" htmlFor="program-name" required>
                            <Input
                                id="program-name"
                                value={programForm.name}
                                onChange={(e) => setProgramForm({ ...programForm, name: e.target.value })}
                                placeholder="e.g. Nursing Internship"
                                className={modalInputClass}
                            />
                        </ModalField>
                        <ModalField label="Description" htmlFor="program-description">
                            <Textarea
                                id="program-description"
                                value={programForm.description}
                                onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })}
                                placeholder="What this program covers"
                                {...modalDescriptionFieldProps}
                                className={modalTextareaClass}
                            />
                        </ModalField>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <ModalField label="Department" htmlFor="program-department">
                                <Select
                                    id="program-department"
                                    className={SELECT_CLASS}
                                    size="large"
                                    placeholder="All departments"
                                    allowClear
                                    options={departments}
                                    value={programForm.department_id || undefined}
                                    onChange={(v) => setProgramForm({ ...programForm, department_id: v || null })}
                                />
                            </ModalField>
                            <ModalField label="Duration (weeks)" htmlFor="program-duration">
                                <Input
                                    id="program-duration"
                                    type="number"
                                    min={1}
                                    placeholder="e.g. 12"
                                    value={programForm.duration_weeks}
                                    onChange={(e) => setProgramForm({ ...programForm, duration_weeks: e.target.value })}
                                    className={modalInputClass}
                                />
                            </ModalField>
                        </div>
                        <ModalField
                            label="Default mentor"
                            htmlFor="program-mentor"
                            hint="Used when an enrolment does not pick a mentor."
                        >
                            <Select
                                id="program-mentor"
                                className={SELECT_CLASS}
                                size="large"
                                placeholder="Select mentor (optional)"
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                options={employees}
                                value={programForm.default_mentor_id || undefined}
                                onChange={(v) => setProgramForm({ ...programForm, default_mentor_id: v || null })}
                            />
                        </ModalField>
                    </form>
                )}
            </DialogModal>

            <DialogModal
                open={!!phaseProgram && !phaseForm}
                onClose={() => setPhaseProgram(null)}
                title={`Phases · ${phaseProgram?.name || ""}`}
                showActions
                hideSaveButton
                closeLabel="Close"
                width="640px"
            >
                {phaseProgram && (
                    <div className="space-y-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Enrolling an intern assigns every training listed on these phases.
                        </p>
                        {(phaseProgram.phases || []).length === 0 ? (
                            <p className="text-sm text-muted-foreground py-4 text-center">No phases yet. Add the first phase to define the intern path.</p>
                        ) : (
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs text-muted-foreground">
                                        <tr>
                                            <th className="text-left font-medium px-3 py-2 w-12">#</th>
                                            <th className="text-left font-medium px-3 py-2">Phase</th>
                                            <th className="text-left font-medium px-3 py-2">Duration</th>
                                            <th className="text-left font-medium px-3 py-2">Trainings</th>
                                            {canManagePrograms && <th className="px-3 py-2 w-20" />}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(phaseProgram.phases || []).map((ph) => (
                                            <tr key={ph.id} className="border-t border-gray-200 dark:border-gray-700">
                                                <td className="px-3 py-2 text-muted-foreground">{ph.sequence}</td>
                                                <td className="px-3 py-2">
                                                    <div className="font-medium">{ph.name}</div>
                                                    {ph.description ? (
                                                        <div className="text-xs text-muted-foreground line-clamp-1">{ph.description}</div>
                                                    ) : null}
                                                </td>
                                                <td className="px-3 py-2">{ph.duration_weeks ? `${ph.duration_weeks} weeks` : "-"}</td>
                                                <td className="px-3 py-2">
                                                    {ph.tasks?.length || 0}
                                                    {ph.tasks?.length ? (
                                                        <span className="text-xs text-muted-foreground"> · {ph.tasks.map((t) => t.title).join(", ")}</span>
                                                    ) : null}
                                                </td>
                                                {canManagePrograms && (
                                                    <td className="px-3 py-2">
                                                        <div className="flex justify-end gap-1">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => setPhaseForm({
                                                                    id: ph.id,
                                                                    program_id: phaseProgram.id,
                                                                    name: ph.name,
                                                                    description: ph.description || "",
                                                                    sequence: ph.sequence,
                                                                    duration_weeks: ph.duration_weeks ?? "",
                                                                    task_ids: (ph.tasks || []).map((t) => t.id),
                                                                })}
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="text-red-500 hover:text-red-600"
                                                                onClick={() => setDeleteTarget({ kind: "phase", id: ph.id, name: ph.name })}
                                                            >
                                                                <Trash className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {canManagePrograms && (
                            <Button
                                variant="outline"
                                onClick={() => setPhaseForm({
                                    ...EMPTY_PHASE,
                                    program_id: phaseProgram.id,
                                    sequence: (phaseProgram.phases?.length || 0) + 1,
                                })}
                            >
                                <Plus className="h-4 w-4 mr-1" /> Add Phase
                            </Button>
                        )}
                    </div>
                )}
            </DialogModal>

            <DialogModal
                open={!!phaseForm}
                onClose={() => setPhaseForm(null)}
                title={phaseForm?.id ? "Edit Phase" : "Add Phase"}
                onSave={savePhase}
                saveLabel={phaseForm?.id ? "Update" : "Save"}
                closeLabel="Cancel"
                editMode={!!phaseForm?.id}
                loading={saving}
                width="600px"
            >
                {phaseForm && (
                    <form className="space-y-4">
                        <ModalField label="Phase name" htmlFor="phase-name" required>
                            <Input
                                id="phase-name"
                                value={phaseForm.name}
                                onChange={(e) => setPhaseForm({ ...phaseForm, name: e.target.value })}
                                placeholder="e.g. Orientation"
                                className={modalInputClass}
                            />
                        </ModalField>
                        <ModalField label="Description" htmlFor="phase-description">
                            <Textarea
                                id="phase-description"
                                value={phaseForm.description}
                                onChange={(e) => setPhaseForm({ ...phaseForm, description: e.target.value })}
                                placeholder="What the intern should complete in this phase"
                                {...modalDescriptionFieldProps}
                                className={modalTextareaClass}
                            />
                        </ModalField>
                        <div className="grid grid-cols-2 gap-4">
                            <ModalField label="Order" htmlFor="phase-order" required>
                                <Input
                                    id="phase-order"
                                    type="number"
                                    min={1}
                                    value={phaseForm.sequence}
                                    onChange={(e) => setPhaseForm({ ...phaseForm, sequence: Number(e.target.value) })}
                                    className={modalInputClass}
                                />
                            </ModalField>
                            <ModalField label="Duration (weeks)" htmlFor="phase-duration">
                                <Input
                                    id="phase-duration"
                                    type="number"
                                    min={1}
                                    placeholder="Optional"
                                    value={phaseForm.duration_weeks}
                                    onChange={(e) => setPhaseForm({ ...phaseForm, duration_weeks: e.target.value })}
                                    className={modalInputClass}
                                />
                            </ModalField>
                        </div>
                        <ModalField
                            label="Trainings in this phase"
                            hint="These trainings are assigned when an intern is enrolled."
                        >
                            <Select
                                className={SELECT_CLASS}
                                size="large"
                                mode="multiple"
                                placeholder="Select trainings"
                                optionFilterProp="label"
                                options={tasks}
                                value={phaseForm.task_ids}
                                onChange={(v) => setPhaseForm({ ...phaseForm, task_ids: v })}
                            />
                        </ModalField>
                    </form>
                )}
            </DialogModal>

            <ConfirmPopupModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
                title={deleteTarget?.kind === "phase" ? "Remove phase" : "Remove program"}
                description={`Remove "${deleteTarget?.name || ""}"? This cannot be undone.`}
                confirmText="Remove"
            />
        </ContainerCard>
    );
};

export default InternshipPrograms;
